import { useCallback, useEffect, useMemo, useRef } from 'react';
import { GroupBase } from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';

import asyncSelectComponents, { LoadingMessage } from './async-override';

/** Пауза в наборе, после которой уходит запрос: AsyncSelect зовёт `loadOptions` на каждый ввод. */
export const SEARCH_DEBOUNCE_MS = 350;

export type UDAsyncSelectProps<Option, IsMulti extends boolean = false> = Omit<
  AsyncProps<Option, IsMulti, GroupBase<Option>>,
  'loadOptions'
> & {
  /** Поиск. Только промис-форма — колбэк react-select не поддерживается. */
  loadOptions: (inputValue: string) => Promise<readonly Option[]>;
  /** 0 отключает задержку. */
  debounceMs?: number;
};

/**
 * Откладывает поиск до паузы в наборе: пока пользователь печатает, таймер сбрасывается,
 * и запрос уходит один — с последним введённым значением.
 *
 * Промисы отменённых вызовов остаются неразрешёнными: react-select ждёт только последний,
 * а разрешать их пустым списком нельзя — в выдаче моргнёт «Нет совпадений».
 *
 * Таймер и сам `loadOptions` живут в ref'ах, поэтому возвращаемая функция стабильна и
 * переживает перерисовку даже с немемоизированным `loadOptions`.
 */
function useDebouncedLoadOptions<Option>(loadOptions: (inputValue: string) => Promise<readonly Option[]>, delay: number) {
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;

  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timerRef.current), []);

  return useCallback(
    (inputValue: string) =>
      new Promise<readonly Option[]>((resolve) => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => resolve(loadOptionsRef.current(inputValue)), delay);
      }),
    [delay],
  );
}

/**
 * Асинхронный select приложения: поиск по мере ввода поверх `react-select/async`.
 *
 * Берёт на себя то, что одинаково у всех поисков — задержку перед запросом, кэш уже
 * найденного и оформление выдачи. Что и где искать, знает вызывающая сторона: она
 * передаёт `loadOptions` и доменный `SingleValue` в `components`.
 */
export function UDAsyncSelect<Option, IsMulti extends boolean = false>(props: UDAsyncSelectProps<Option, IsMulti>) {
  const { loadOptions, debounceMs = SEARCH_DEBOUNCE_MS, components, ...restProps } = props;

  const debouncedLoadOptions = useDebouncedLoadOptions(loadOptions, debounceMs);
  const selectComponents = useMemo(() => ({ ...asyncSelectComponents, ...components }), [components]);

  return (
    <AsyncSelect<Option, IsMulti, GroupBase<Option>>
      cacheOptions
      loadingMessage={LoadingMessage}
      {...restProps}
      loadOptions={debouncedLoadOptions}
      components={selectComponents}
    />
  );
}
